import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth } from "@/lib/api";
import { BrandingProvider } from "@/lib/branding";
import Login from "@/pages/Login";
import Security from "@/pages/Security";
import MyTrainings from "@/pages/MyTrainings";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Masters from "@/pages/Masters";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import TrainingCard from "@/pages/TrainingCard";
import Questionnaires from "@/pages/Questionnaires";
import QuestionnaireEditor from "@/pages/QuestionnaireEditor";
import Assignments from "@/pages/Assignments";
import Reports from "@/pages/Reports";
import Scoreboard from "@/pages/Scoreboard";
import QuizEntry from "@/pages/QuizEntry";
import QuizPlayer from "@/pages/QuizPlayer";
import AdminLayout from "@/components/AdminLayout";
import Profile from "@/pages/Profile";
import PublicAttend from "@/pages/PublicAttend";
import Attendance from "@/pages/Attendance";
import TrainingViewer from "@/pages/TrainingViewer";
import Events from "@/pages/Events";
import EventKiosk from "@/pages/EventKiosk";
import TrainingHistory from "@/pages/TrainingHistory";
import VersionWatcher from "@/components/VersionWatcher";
import CertificateVerify from "@/pages/CertificateVerify";

const EMPLOYEE_ROLES = ["employee", "user"];

function RequireAuth({ children, roles, employeeOnly }) {
  const [state, setState] = useState({ loading: true, user: null });
  useEffect(() => {
    if (!auth.token()) { setState({ loading: false, user: null }); return; }
    auth.me().then(u => setState({ loading: false, user: u })).catch(() => setState({ loading: false, user: null }));
  }, []);
  if (state.loading) return <div className="h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!state.user) return <Navigate to="/login" replace />;
  const isEmployee = EMPLOYEE_ROLES.includes(state.user.role);
  if (employeeOnly && !isEmployee) {
    return <Navigate to="/dashboard" replace />;
  }
  if (roles === "admin" && isEmployee) {
    return <Navigate to="/my" replace />;
  }
  if (Array.isArray(roles) && !roles.includes(state.user.role)) {
    if (isEmployee) return <Navigate to="/my" replace />;
    return <div className="h-screen grid place-items-center"><div className="text-center"><div className="text-2xl font-bold">Access Denied</div><div className="text-slate-500 mt-2">Your role does not have access to this page.</div></div></div>;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrandingProvider>
        <VersionWatcher/>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/employee/login" element={<Navigate to="/login" replace />} />
            {/* Public quiz routes */}
            <Route path="/q/:token" element={<QuizEntry />} />
            <Route path="/play/:attemptId" element={<QuizPlayer />} />
            <Route path="/training/:token" element={<TrainingViewer />} />
            <Route path="/attend" element={<PublicAttend />} />
            {/* Public event kiosk (QR scan → attendance) */}
            <Route path="/kiosk/event/:eventId" element={<EventKiosk />} />
            <Route path="/verify/cert/:id" element={<CertificateVerify />} />

            {/* Employee portal */}
            <Route path="/my" element={<RequireAuth employeeOnly><MyTrainings /></RequireAuth>} />
            <Route path="/my/card" element={<RequireAuth employeeOnly><TrainingCard self /></RequireAuth>} />
            <Route path="/my/history" element={<RequireAuth employeeOnly><TrainingHistory self /></RequireAuth>} />

            {/* Self-service profile — any authenticated user */}
            <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />

            {/* Admin routes — any role that isn't 'employee'/'user' */}
            <Route element={<RequireAuth roles="admin"><AdminLayout /></RequireAuth>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/employees/:id" element={<Scoreboard />} />
              <Route path="/masters" element={<Masters />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/integrations" element={<Integrations />} />
              <Route path="/security" element={<Security />} />
              <Route path="/employees/:id/card" element={<TrainingCard />} />
              <Route path="/questionnaires" element={<Questionnaires />} />
              <Route path="/questionnaires/new" element={<QuestionnaireEditor />} />
              <Route path="/questionnaires/:id" element={<QuestionnaireEditor />} />
              <Route path="/assignments" element={<Assignments />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/events" element={<Events />} />
              <Route path="/employees/:id/history" element={<TrainingHistory />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </BrandingProvider>
    </div>
  );
}
